import React from 'react';

import DefaultAvatar from 'assets/chat/img.svg';
import { formatChatListUnreadCount } from 'common-ui/chat/constants';
import { formatChatListTime } from 'utils/dateUtils';
import { getEmailPreview, parseEmailMessage } from 'utils/emailUtils';

import {
  ChatListBlock,
  ChatListItem,
  ChatListItemBlock,
  ChatListItemImg,
  ChatListItemLeftSide,
  ChatListItemRightSide,
  ChatListItemText,
  ChatListItemTime,
  ChatListItemTitle,
  ChatListItemTitleBlock,
  UnreadBadge,
} from './ChatList.styles';

const getSenderName = (sender) => {
  if (sender?.name && sender?.surname) {
    return `${sender.name} ${sender.surname}`;
  }
  if (sender?.name) {
    return sender.name;
  }
  return `User ${sender?.id?.slice(-6) || 'Unknown'}`;
};

const getMessagePreview = (conversation) => {
  // If it's an IMAP message, format it nicely
  if (conversation.platform === 'imap') {
    // Try to use lastMessageObject if available, otherwise use lastMessage string
    const messageData = conversation.lastMessageObject || {
      messageText: conversation.lastMessage,
      platform: 'imap',
    };

    const emailData = parseEmailMessage(messageData);
    if (emailData?.subject && emailData?.body) {
      const bodyPreview = getEmailPreview(emailData.body, 50);
      return `${emailData.subject}: ${bodyPreview}`;
    } else if (emailData?.subject) {
      // If only subject is available
      return emailData.subject;
    }
  }

  // For other platforms, return the lastMessage as is
  return conversation.lastMessage || 'No messages yet';
};

const ChatList = ({ conversations = [], loading, onConversationSelect }) => {
  if (loading) {
    return (
      <ChatListBlock>
        <ChatListItem>
          <ChatListItemBlock>
            <ChatListItemText>Loading conversations...</ChatListItemText>
          </ChatListItemBlock>
        </ChatListItem>
      </ChatListBlock>
    );
  }

  if (conversations.length === 0) {
    return (
      <ChatListBlock>
        <ChatListItem>
          <ChatListItemBlock>
            <ChatListItemText>Select a platform to view conversations</ChatListItemText>
          </ChatListItemBlock>
        </ChatListItem>
      </ChatListBlock>
    );
  }

  return (
    <ChatListBlock>
      {conversations.map((conversation) => {
        const hasUnread = conversation.unreadCount > 0;

        return (
          <ChatListItem
            key={conversation.conversationId}
            onClick={() => onConversationSelect?.(conversation)}
          >
            <ChatListItemBlock>
              <ChatListItemLeftSide>
                <ChatListItemImg src={conversation.sender?.avatar || DefaultAvatar} />
                <ChatListItemTitleBlock>
                  <ChatListItemTitle>{getSenderName(conversation.sender)}</ChatListItemTitle>
                  <ChatListItemText $hasUnread={hasUnread}>
                    {getMessagePreview(conversation)}
                  </ChatListItemText>
                </ChatListItemTitleBlock>
              </ChatListItemLeftSide>
              <ChatListItemRightSide>
                {hasUnread && (
                  <UnreadBadge>
                    {formatChatListUnreadCount(conversation.unreadCount)}
                  </UnreadBadge>
                )}
                <ChatListItemTime>
                  {formatChatListTime(conversation.lastMessageAt)}
                </ChatListItemTime>
              </ChatListItemRightSide>
            </ChatListItemBlock>
          </ChatListItem>
        );
      })}
    </ChatListBlock>
  );
};

export default ChatList;
